import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useVehiclePartnerActions } from '@/hooks/use-vehicle-partner-actions';
import { findPartnerLinkedSource } from './funding-sources-coordination/partner-funding-utils';
import { MirroredSourceStatus } from './funding-sources-coordination/MirroredSourceStatus';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useIsMobile,
} from '@design-system';
import {
  SessionType,
  isNullOrUndefined,
  formatCurrency as sharedFormatCurrency,
  carePhaseDefList,
} from '@shared';
import { HelpCircle, Info } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { CurrencyInput, PercentInput } from '..';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '../model';
import { FormErrorAlert } from './FormErrorAlert';
import {
  PartnerAllocationSection,
  type AllocationOption,
} from './funding-sources-coordination/partner-allocation-section';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { Input } from './ui/input';
import {
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  SELF_FUNDING_ASSET_TYPES,
  getAssetTypeIdFromAccountType,
  getCalcFormulaInfo,
  getFieldLabels,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';
import { CalcFormulaSheetContent } from '@/features/guided-financial-planning/components/calc-formula-sheet-content';
import {
  CURRENT_YEAR,
  YearPresetSelect,
  CarePhaseYearSelect,
  type CarePhaseYears,
} from '@/features/guided-financial-planning/components/year-select';

interface SelfFundingDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundingSourceId?: string;
}

type SelfFundingKey = keyof SelfFundingSource;

const formatCurrency = (value: number) => sharedFormatCurrency(value, 0);

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-sm text-red-400">{message}</p>;
}

function FieldLabel({
  label,
  tooltip,
  className,
}: {
  label: string;
  tooltip: string | ReactNode;
  className?: string;
}) {
  return (
    <Label className={cn('block text-sm font-medium', className)}>
      {label}{' '}
      <Tooltip>
        <TooltipTrigger className="inline-block -translate-y-px align-middle">
          <Info className="text-muted-foreground size-3.5 shrink-0 cursor-help" />
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs"
        >
          <p className="text-sm">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </Label>
  );
}

function getInitialAssetType(accountType: string | null | undefined): AssetTypeId | 'other' {
  const assetTypeId = getAssetTypeIdFromAccountType(accountType);
  if (assetTypeId && assetTypeId !== 'existing-ltc-policy') return assetTypeId;
  return 'other';
}

function getInitialYearlyContribution(
  assetType: AssetTypeId | 'other',
  monthlyAmount: number | null | undefined,
): number | null {
  if (assetType !== 'other' && assetType !== 'retirement-income' && monthlyAmount) {
    return monthlyAmount * 12;
  }
  return null;
}

export function SelfFundingDetailsModal({
  open,
  setOpen,
  fundingSourceId,
}: SelfFundingDetailsModalProps) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { partnerFirstName, partnerClientId, saveWithPartnerSync, handleUnlink } =
    useVehiclePartnerActions();
  const { flags } = useFeatureFlags();

  const {
    clientId,
    multipleFundingSources,
    intakeSurvey: { clientHasStartedLtc, clientBirthDate },
    appliedInferenceSet: { ltcAtYear, yearsTillLtc, ltcAtAge },
    allPhaseCosts,
  } = client;

  const selfFunding =
    fundingSourceId && multipleFundingSources.selfFunding[fundingSourceId]
      ? multipleFundingSources.selfFunding[fundingSourceId]
      : ({
          hasSelfFunding: false,
          existingAssetsValue: null,
          annualRateOfReturn: null,
          contributionStartYear: new Date().getFullYear(),
          contributionEndYear: ltcAtYear,
          monthlyContributionAmount: null,
          gainsTaxRate: null,
          monthlyIncome: null,
          contributionMonthCount: 0,
        } as SelfFundingSource);

  // Account types that already have a funding source (excluding the one being edited)
  const usedAccountTypes = useMemo(() => {
    const used = new Set<string>();
    Object.entries(multipleFundingSources.selfFunding).forEach(([id, source]) => {
      if (id === fundingSourceId) return;
      if (source.accountType) used.add(source.accountType);
    });
    return used;
  }, [multipleFundingSources.selfFunding, fundingSourceId]);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const isExistingMirror = selfFunding?.partnerAction === 'mirrored';
  const existingSplitRatio = selfFunding?.splitRatio ?? null;

  const partnerLinkedSource =
    isExistingMirror && selfFunding?.fundingSourceGroupId
      ? findPartnerLinkedSource(
          client.mutableClientPartner?.multipleFundingSources?.selfFunding,
          selfFunding.fundingSourceGroupId,
        )?.[1]
      : undefined;

  const hasSplitWithPartner = isExistingMirror && existingSplitRatio != null && partnerLinkedSource;
  const initialTotalAssets = hasSplitWithPartner
    ? (selfFunding.existingAssetsValue ?? 0) + (partnerLinkedSource.existingAssetsValue ?? 0)
    : selfFunding.existingAssetsValue;
  const initialTotalContribution = hasSplitWithPartner
    ? (selfFunding.monthlyContributionAmount ?? 0) + (partnerLinkedSource.monthlyContributionAmount ?? 0)
    : selfFunding.monthlyContributionAmount;
  const initialTotalIncome = hasSplitWithPartner
    ? (selfFunding.monthlyIncome ?? 0) + (partnerLinkedSource.monthlyIncome ?? 0)
    : selfFunding.monthlyIncome;
  const initialTotalDrawdown = hasSplitWithPartner
    ? (selfFunding.drawdownAmount ?? 0) + (partnerLinkedSource.drawdownAmount ?? 0)
    : selfFunding.drawdownAmount;

  const initialSelfFundingValue: SelfFundingSource = {
    ...selfFunding,
    existingAssetsValue: initialTotalAssets,
    monthlyContributionAmount: initialTotalContribution,
    monthlyIncome: initialTotalIncome,
    drawdownAmount: initialTotalDrawdown,
    ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
  };

  const [selfFundingState, setSelfFundingState] =
    useState<SelfFundingSource>(initialSelfFundingValue);
  const [formErrors, setFormErrors] = useState({} as Record<SelfFundingKey, string>);
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const [initialSelfFundingState, setInitialSelfFundingState] =
    useState<SelfFundingSource>(initialSelfFundingValue);
  const [showWarning, setShowWarning] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [mirrorChecked, setMirrorChecked] = useState(false);
  const [allocationOption, setAllocationOption] = useState<AllocationOption>('mirror');
  const [customClientPercent, setCustomClientPercent] = useState(50);
  const [showFormulaSheet, setShowFormulaSheet] = useState(false);

  const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeId | 'other'>(() =>
    getInitialAssetType(selfFunding?.accountType),
  );
  const [yearlyContributionValue, setYearlyContributionValue] = useState<number | null>(() =>
    getInitialYearlyContribution(
      getInitialAssetType(selfFunding?.accountType),
      selfFunding?.monthlyContributionAmount,
    ),
  );

  const isRetirementIncome = selectedAssetType === 'retirement-income';
  const isOther = selectedAssetType === 'other';
  const isSpecificAssetType = !isOther;

  const fieldLabels = (() => {
    if (selectedAssetType === 'other') return null;
    const labels = getFieldLabels(selectedAssetType);
    if (selectedAssetType === 'retirement-income' && ltcAtAge != null) {
      return {
        ...labels,
        currentValue: {
          ...labels.currentValue,
          label: `PROJECTED ANNUAL INCOME AT LTC START AGE (${ltcAtAge})`,
        },
      };
    }
    return labels;
  })();

  const formulaAssetTypeId: AssetTypeId =
    selectedAssetType === 'other' ? 'brokerage' : selectedAssetType;
  const formulaInfo = getCalcFormulaInfo(formulaAssetTypeId);
  const assetLabel = isOther
    ? (selfFundingState.selfFundingName || 'Self-funding')
    : (SELF_FUNDING_ASSET_TYPES.find(t => t.id === selectedAssetType)?.label ?? 'Self-funding');

  const carePhaseYears: CarePhaseYears = useMemo(() => {
    const startYears = client?.clientPhasePredictedStartYears;
    if (!startYears) return { earlyCare: null, moderateCare: null, fullCare: null };
    return {
      earlyCare: startYears[carePhaseDefList[0].value] ?? null,
      moderateCare: startYears[carePhaseDefList[1].value] ?? null,
      fullCare: startYears[carePhaseDefList[2].value] ?? null,
    };
  }, [client?.clientPhasePredictedStartYears]);

  const allPhaseCareMonthsNeeded = allPhaseCosts?.allPhaseCareMonthsNeeded;
  const ltcEndYear = useMemo(() => {
    if (!ltcAtYear || !allPhaseCareMonthsNeeded) return null;
    return Math.round(ltcAtYear + allPhaseCareMonthsNeeded / 12);
  }, [ltcAtYear, allPhaseCareMonthsNeeded]);

  const requiredKeys: SelfFundingKey[] = (() => {
    if (isOther) {
      return clientHasStartedLtc
        ? ['existingAssetsValue']
        : ['existingAssetsValue', 'annualRateOfReturn', 'gainsTaxRate'];
    }
    if (isRetirementIncome) {
      return ['existingAssetsValue', 'gainsTaxRate'];
    }
    return ['existingAssetsValue', 'annualRateOfReturn', 'gainsTaxRate'];
  })();

  const showPartnerSection =
    Boolean(partnerFirstName) && Boolean(partnerClientId) && flags.selfFundingPartnerSync === true;
  const clientName = client.clientFirstName ?? 'Client';
  const totalAssets = selfFundingState.existingAssetsValue ?? 0;

  const handleChangeSelfFunding = (selfFundingKey: SelfFundingKey) => (value: number) => {
    setSelfFundingState(prev => ({ ...prev, [selfFundingKey]: value }));
  };

  function handleChangeSelfFundingName(value: string) {
    setSelfFundingState(prev => ({ ...prev, selfFundingName: value }));
  }

  function handleAssetTypeChange(value: string) {
    const newType = value as AssetTypeId | 'other';
    const prevType = selectedAssetType;
    setSelectedAssetType(newType);

    // Retirement Income → Other: move annual income to monthly income fields
    if (prevType === 'retirement-income' && newType === 'other') {
      setSelfFundingState(prev => ({
        ...prev,
        monthlyIncome: prev.existingAssetsValue != null ? prev.existingAssetsValue / 12 : null,
        monthlyIncomeTaxRate: prev.gainsTaxRate ?? null,
        existingAssetsValue: null,
        gainsTaxRate: null,
      }));
      return;
    }

    // Other → Retirement Income: move monthly income to annual income fields
    if (prevType === 'other' && newType === 'retirement-income') {
      setSelfFundingState(prev => ({
        ...prev,
        existingAssetsValue: prev.monthlyIncome != null ? prev.monthlyIncome * 12 : null,
        gainsTaxRate: prev.monthlyIncomeTaxRate ?? null,
        monthlyIncome: null,
        monthlyIncomeTaxRate: null,
      }));
      return;
    }

    if (newType !== 'other' && newType !== 'retirement-income') {
      setYearlyContributionValue(
        selfFundingState.monthlyContributionAmount
          ? selfFundingState.monthlyContributionAmount * 12
          : null,
      );
    }
  }

  function handleToggleContributionStop(checked: boolean) {
    if (checked) {
      setSelfFundingState(prev => ({ ...prev, contributionStopYear: CURRENT_YEAR + 5 }));
    } else {
      setSelfFundingState(prev => ({ ...prev, contributionStopYear: null }));
    }
  }

  function handleToggleDrawdown(checked: boolean) {
    setSelfFundingState(prev => ({
      ...prev,
      hasDrawdown: checked,
      ...(!checked && {
        drawdownStartAtYear: null,
        drawdownEndAtYear: null,
        drawdownAmount: null,
        drawdownAmountInterval: null,
      }),
      ...(checked &&
        !prev.drawdownAmountInterval && {
          drawdownAmountInterval: 'yearly',
        }),
    }));
  }

  function handleChangeYearlyContribution(value: number) {
    setYearlyContributionValue(value);
    setSelfFundingState(prev => ({ ...prev, monthlyContributionAmount: value / 12 }));
  }

  function validateForm() {
    const errors = {} as Record<SelfFundingKey, string>;
    requiredKeys.forEach(key => {
      if (isNullOrUndefined(selfFundingState[key])) {
        errors[key] = 'Required Field';
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleClickSubmit() {
    setFormHasBeenSubmitted(true);
    if (!validateForm()) return;

    const assetLabel = isOther
      ? undefined
      : SELF_FUNDING_ASSET_TYPES.find(t => t.id === selectedAssetType)?.label;

    const hasDrawdown = !!selfFundingState.hasDrawdown;

    const updatedSelfFunding: SelfFundingSource = {
      ...selfFundingState,
      hasSelfFunding: true,
      selfFundingName: isOther
        ? selfFundingState.selfFundingName || 'Self-funding'
        : (assetLabel ?? 'Self-funding'),
      accountType: isOther
        ? (selfFundingState.accountType ?? null)
        : ASSET_TYPE_TO_ACCOUNT_TYPE[selectedAssetType as AssetTypeId],
      ...(isSpecificAssetType &&
        !isRetirementIncome && {
          monthlyContributionAmount: (yearlyContributionValue ?? 0) / 12,
          monthlyIncome: null,
          monthlyIncomeTaxRate: null,
        }),
      ...(isRetirementIncome && {
        annualRateOfReturn: 0,
        monthlyContributionAmount: 0,
        monthlyIncome: null,
        monthlyIncomeTaxRate: null,
        contributionStopYear: null,
        hasDrawdown: false,
        drawdownStartAtYear: null,
        drawdownEndAtYear: null,
        drawdownAmount: null,
        drawdownAmountInterval: null,
      }),
      ...(!hasDrawdown && {
        drawdownStartAtYear: null,
        drawdownEndAtYear: null,
        drawdownAmount: null,
        drawdownAmountInterval: null,
      }),
    };

    const shouldSyncPartner =
      (isExistingMirror && fundingSourceId) || (mirrorChecked && partnerClientId);

    if (shouldSyncPartner) {
      saveWithPartnerSync({
        fundingSourceId,
        source: updatedSelfFunding,
        isExistingMirror,
        existingSplitRatio,
        allocationOption,
        clientPercent: customClientPercent,
      });
    } else {
      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId,
          fundingSourceType: 'selfFunding',
          fundingSourceData: updatedSelfFunding,
          fundingSourceId: fundingSourceId ?? null,
        }),
      );
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('downloadReport', 'true');
    setSearchParams(newParams, { replace: true });
    setOpen(false);
  }

  const formIsValid = Object.keys(formErrors).length === 0;
  const hasRequiredFieldsErrors = Object.keys(formErrors).some(key =>
    requiredKeys.includes(key as SelfFundingKey),
  );

  function resetForm() {
    setSelfFundingState(selfFunding ?? null);
    setFormErrors({} as Record<SelfFundingKey, string>);
    setMirrorChecked(false);
    setAllocationOption('mirror');
    setCustomClientPercent(50);
    const initialType = getInitialAssetType(selfFunding?.accountType);
    setSelectedAssetType(initialType);
    setYearlyContributionValue(
      getInitialYearlyContribution(initialType, selfFunding?.monthlyContributionAmount),
    );
  }

  function onClose() {
    if (JSON.stringify(selfFundingState) !== JSON.stringify(initialSelfFundingState)) {
      setShowWarning(true);
    } else {
      setOpen(false);
      resetForm();
    }
  }

  function handleConfirmClose() {
    setShowWarning(false);
    setOpen(false);
    resetForm();
  }

  function handleCancelClose() {
    setShowWarning(false);
  }

  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [
    selfFundingState.annualRateOfReturn,
    selfFundingState.existingAssetsValue,
    selfFundingState.gainsTaxRate,
    selectedAssetType,
  ]);

  useEffect(() => {
    if (open) {
      const reconstructedAssets = hasSplitWithPartner
        ? (selfFunding.existingAssetsValue ?? 0) + (partnerLinkedSource.existingAssetsValue ?? 0)
        : selfFunding.existingAssetsValue;

      let openingSelfFundingState: SelfFundingSource = selfFunding
        ? { ...selfFunding, hasSelfFunding: true, existingAssetsValue: reconstructedAssets }
        : ({ hasSelfFunding: false } as SelfFundingSource);

      if (isAdvisorSession) {
        openingSelfFundingState = {
          ...openingSelfFundingState,
          addedByAdvisorId: advisorId,
        };
      }

      setSelfFundingState(openingSelfFundingState);
      setInitialSelfFundingState(openingSelfFundingState);
      setFormHasBeenSubmitted(false);
      setMirrorChecked(false);
      setAllocationOption('mirror');
      setCustomClientPercent(selfFunding?.splitRatio ?? 50);
      const initialType = getInitialAssetType(selfFunding?.accountType);
      setSelectedAssetType(initialType);
      setYearlyContributionValue(
        getInitialYearlyContribution(initialType, selfFunding?.monthlyContributionAmount),
      );
    }
  }, [open]);

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingState, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded: allPhaseCosts?.allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const showPreview =
    flags.selfFundingGrowthPreview === true &&
    !clientHasStartedLtc &&
    projectedValue != null &&
    selfFundingState != null;

  const formContent = (
    <div
      className="space-y-5"
      data-intercom-target="Self Funding Fields"
    >
      {showPreview && (
        <Alert
          variant="info"
          className="bg-[#F8F6FD]"
        >
          <Info className="size-4" />
          <AlertDescription className="flex items-center justify-between gap-2">
            <span className="whitespace-nowrap">
              {ageAtLtc != null
                ? `Projected value by age ${ageAtLtc}: `
                : `Projected value by ${ltcAtYear}: `}
              <span className="font-semibold">{formatCompactCurrency(projectedValue)}</span>
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowFormulaSheet(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') setShowFormulaSheet(true);
                  }}
                  className="text-muted-foreground hover:text-primary shrink-0 cursor-pointer"
                >
                  <HelpCircle className="size-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>How is this calculated?</p>
              </TooltipContent>
            </Tooltip>
          </AlertDescription>
        </Alert>
      )}

      {isExistingMirror && partnerFirstName && (
        <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5">
          <Info className="mt-0.5 size-4 shrink-0 text-blue-600" />
          <p className="text-xs text-blue-800">
            You&apos;re editing the combined total for {clientName} and {partnerFirstName}.{' '}
            {existingSplitRatio != null
              ? `The ${existingSplitRatio}/${100 - existingSplitRatio} split will be applied when you save.`
              : 'Both will receive the full amount when you save.'}
          </p>
        </div>
      )}

      <div
        className={cn('grid grid-cols-1 gap-4', {
          'grid-cols-2': isOther,
        })}
      >
        <div className="space-y-1">
          <FieldLabel
            className="text-xs"
            label="Account Type"
            tooltip="Select the type of account or asset to allocate for long-term care funding"
          />
          <Select
            value={selectedAssetType}
            onValueChange={handleAssetTypeChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select account type" />
            </SelectTrigger>
            <SelectContent>
              {SELF_FUNDING_ASSET_TYPES.map(t => {
                const isUsed = usedAccountTypes.has(ASSET_TYPE_TO_ACCOUNT_TYPE[t.id]);
                return (
                  <SelectItem
                    key={t.id}
                    value={t.id}
                    disabled={isUsed}
                    className="flex w-full items-center justify-between gap-2"
                  >
                    {t.label}
                    {isUsed && (
                      <Badge
                        variant="secondary"
                        className="px-1.5 py-0.5 text-[10px]"
                      >
                        Added
                      </Badge>
                    )}
                  </SelectItem>
                );
              })}
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isOther && (
          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label="Name (optional)"
              tooltip="Optional name to identify this self-funding source"
            />
            <Input
              type="text"
              placeholder="Self-funding"
              value={selfFundingState.selfFundingName ?? ''}
              onChange={e => handleChangeSelfFundingName(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Specific asset type fields (non-retirement) */}
      {isSpecificAssetType && !isRetirementIncome && fieldLabels && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label={fieldLabels.currentValue.label}
              tooltip="Present-day value of this account or asset"
            />
            <CurrencyInput
              label=""
              placeholder={fieldLabels.currentValue.placeholder}
              value={selfFundingState.existingAssetsValue}
              onChange={handleChangeSelfFunding('existingAssetsValue')}
              onPreview={handleChangeSelfFunding('existingAssetsValue')}
            />
            <ErrorMessage message={formErrors.existingAssetsValue} />
          </div>

          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label={fieldLabels.yearlyContribution.label}
              tooltip="Amount added each year to this account"
            />
            <CurrencyInput
              label=""
              placeholder={fieldLabels.yearlyContribution.placeholder}
              value={yearlyContributionValue}
              onChange={handleChangeYearlyContribution}
              onPreview={handleChangeYearlyContribution}
            />
          </div>

          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label={fieldLabels.rateOfReturn.label}
              tooltip="The expected annual rate of return applied to both existing assets and contributions"
            />
            <PercentInput
              label=""
              placeholder={fieldLabels.rateOfReturn.placeholder}
              value={selfFundingState.annualRateOfReturn}
              onChange={handleChangeSelfFunding('annualRateOfReturn')}
              onPreview={handleChangeSelfFunding('annualRateOfReturn')}
            />
            <ErrorMessage message={formErrors.annualRateOfReturn} />
          </div>

          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label={fieldLabels.taxRate.label}
              tooltip="The estimated tax rate applied when withdrawing from this account"
            />
            <PercentInput
              label=""
              placeholder={fieldLabels.taxRate.placeholder}
              value={selfFundingState.gainsTaxRate}
              onChange={handleChangeSelfFunding('gainsTaxRate')}
              onPreview={handleChangeSelfFunding('gainsTaxRate')}
            />
            <ErrorMessage message={formErrors.gainsTaxRate} />
            {fieldLabels.taxRateHint && (
              <p className="text-muted-foreground mt-1 text-[11px] leading-snug">
                {fieldLabels.taxRateHint}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Retirement income fields (2 fields only) */}
      {isRetirementIncome && fieldLabels && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label={fieldLabels.currentValue.label}
              tooltip="Expected annual income during long-term care years, such as Social Security, pension, or rental income"
            />
            <CurrencyInput
              label=""
              placeholder={fieldLabels.currentValue.placeholder}
              value={selfFundingState.existingAssetsValue}
              onChange={handleChangeSelfFunding('existingAssetsValue')}
              onPreview={handleChangeSelfFunding('existingAssetsValue')}
            />
            <ErrorMessage message={formErrors.existingAssetsValue} />
          </div>

          <div className="space-y-1">
            <FieldLabel
              className="text-xs"
              label={fieldLabels.taxRate.label}
              tooltip="The estimated tax rate that applies to this income"
            />
            <PercentInput
              label=""
              placeholder={fieldLabels.taxRate.placeholder}
              value={selfFundingState.gainsTaxRate}
              onChange={handleChangeSelfFunding('gainsTaxRate')}
              onPreview={handleChangeSelfFunding('gainsTaxRate')}
            />
            <ErrorMessage message={formErrors.gainsTaxRate} />
          </div>
        </div>
      )}

      {isRetirementIncome && (
        <Alert variant="default">
          <Info className="size-4" />
          <AlertDescription>
            This includes retirement income like social security, pension, and rental income. To add
            more retirement income sources, use the “Other” option.
          </AlertDescription>
        </Alert>
      )}

      {/* "Other" type: all original fields */}
      {isOther && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
          {/* Row 1 */}
          <div className="space-y-1">
            <FieldLabel
              label="Available Assets"
              tooltip="Present-day value of assets that can be reserved for future long-term care expenses"
            />
            <CurrencyInput
              label=""
              placeholder="Enter dollars"
              value={selfFundingState.existingAssetsValue}
              onChange={handleChangeSelfFunding('existingAssetsValue')}
              onPreview={handleChangeSelfFunding('existingAssetsValue')}
            />
            <ErrorMessage message={formErrors.existingAssetsValue} />
          </div>

          {!clientHasStartedLtc && (
            <div className="space-y-1">
              <FieldLabel
                label="Annual Rate of Return"
                tooltip="The expected annual rate of return applied to both existing assets and monthly contributions"
              />
              <PercentInput
                label=""
                placeholder="Enter percent"
                value={selfFundingState.annualRateOfReturn}
                onChange={handleChangeSelfFunding('annualRateOfReturn')}
                onPreview={handleChangeSelfFunding('annualRateOfReturn')}
              />
              <ErrorMessage message={formErrors.annualRateOfReturn} />
            </div>
          )}

          {/* Row 2 */}
          {!clientHasStartedLtc && (
            <>
              <div className="space-y-1">
                <FieldLabel
                  label="Gains Tax Rate"
                  tooltip={
                    <>
                      The tax rate on investment profits. For more info, view the{' '}
                      <a
                        href="https://www.irs.gov/taxtopics/tc409"
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        IRS article
                      </a>
                      .
                    </>
                  }
                />
                <PercentInput
                  label=""
                  placeholder="Enter percent"
                  value={selfFundingState.gainsTaxRate}
                  onChange={handleChangeSelfFunding('gainsTaxRate')}
                  onPreview={handleChangeSelfFunding('gainsTaxRate')}
                />
                <ErrorMessage message={formErrors.gainsTaxRate} />
              </div>

              <div className="space-y-1">
                <FieldLabel
                  label="Monthly Contribution (optional)"
                  tooltip="Money added each month to assets or investments, aimed at increasing the total value available to fund long-term care expenses"
                />
                <CurrencyInput
                  label=""
                  placeholder="Enter dollars"
                  value={selfFundingState.monthlyContributionAmount}
                  onChange={handleChangeSelfFunding('monthlyContributionAmount')}
                  onPreview={handleChangeSelfFunding('monthlyContributionAmount')}
                />
              </div>
            </>
          )}

          {/* Row 3 — Retirement Income */}
          <div className="space-y-1.5 sm:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <FieldLabel
                  label={
                    ltcAtAge != null
                      ? `Expected Monthly Income at LTC Start Age (${ltcAtAge})`
                      : 'Expected Monthly Income at LTC Start Age'
                  }
                  tooltip="Expected income during long-term care years, such as Social Security, pension, or rental income that can be allocated to LTC expenses. If the income schedule is not monthly, please calculate and enter the equivalent monthly amount."
                />
                <CurrencyInput
                  label=""
                  placeholder="Enter dollars"
                  value={selfFundingState.monthlyIncome}
                  onChange={handleChangeSelfFunding('monthlyIncome')}
                  onPreview={handleChangeSelfFunding('monthlyIncome')}
                />
              </div>

              <div className="space-y-1">
                <FieldLabel
                  label={
                    ltcAtAge != null
                      ? `Expected Annual Income Tax Rate at LTC Start Age (${ltcAtAge})`
                      : 'Expected Annual Income Tax Rate at LTC Start Age'
                  }
                  tooltip="The estimated tax rate that applies to your monthly income. This reduces the projected value to reflect after-tax income available for care expenses."
                />
                <PercentInput
                  label=""
                  placeholder="Enter percent"
                  value={selfFundingState.monthlyIncomeTaxRate ?? null}
                  onChange={handleChangeSelfFunding('monthlyIncomeTaxRate')}
                  onPreview={handleChangeSelfFunding('monthlyIncomeTaxRate')}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Advanced Options ── */}
      {flags.selfFundingAdvancedOptions && !isRetirementIncome && (
        <div className="mt-4 space-y-4">
          {/* ── Contribution End Date Toggle ── */}
          <div className="space-y-2 border-t pt-4">
            <label
              htmlFor="modal-has-contribution-stop"
              className="flex cursor-pointer items-center justify-between"
            >
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase">
                {fieldLabels?.contributionStop ?? 'End Contributions Early'}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="text-muted-foreground size-3.5 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-xs text-xs font-normal normal-case"
                  >
                    Set a year when assumed contributions to this account will stop, rather than
                    continuing until long-term care begins.
                  </TooltipContent>
                </Tooltip>
              </span>
              <Switch
                id="modal-has-contribution-stop"
                checked={selfFundingState.contributionStopYear != null}
                onCheckedChange={handleToggleContributionStop}
              />
            </label>

            {selfFundingState.contributionStopYear != null && (
              <YearPresetSelect
                label="Stop at"
                defaultLabel="At LTC start (default)"
                value={selfFundingState.contributionStopYear}
                onChange={year =>
                  setSelfFundingState(prev => ({ ...prev, contributionStopYear: year }))
                }
              />
            )}
          </div>

          {/* ── Withdrawals Toggle ── */}
          <div className="space-y-2 border-t pt-4">
            <label
              htmlFor="modal-has-drawdown"
              className="flex cursor-pointer items-center justify-between"
            >
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase">
                Withdrawals
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="text-muted-foreground size-3.5 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-xs text-xs font-normal normal-case"
                  >
                    Expected withdrawals from this account before care begins. This reduces the
                    projected value available for long-term care funding.
                  </TooltipContent>
                </Tooltip>
              </span>
              <Switch
                id="modal-has-drawdown"
                checked={!!selfFundingState.hasDrawdown}
                onCheckedChange={handleToggleDrawdown}
              />
            </label>

            {selfFundingState.hasDrawdown && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <YearPresetSelect
                    label="Withdrawals begin"
                    defaultLabel="Starting now"
                    value={selfFundingState.drawdownStartAtYear ?? null}
                    onChange={year =>
                      setSelfFundingState(prev => ({ ...prev, drawdownStartAtYear: year }))
                    }
                  />

                  <CarePhaseYearSelect
                    label="Withdrawals end"
                    defaultLabel="At LTC start"
                    value={selfFundingState.drawdownEndAtYear ?? null}
                    onChange={year =>
                      setSelfFundingState(prev => ({ ...prev, drawdownEndAtYear: year }))
                    }
                    carePhaseYears={carePhaseYears}
                    ltcEndYear={ltcEndYear}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="inline-block text-[10px] font-semibold uppercase">
                    Withdrawal amount
                  </Label>
                  <div className="flex items-center gap-2">
                    <InputGroup className="flex-1">
                      <InputGroupAddon align="inline-start">$</InputGroupAddon>
                      <InputGroupInput
                        inputMode="numeric"
                        placeholder="Enter amount"
                        value={
                          selfFundingState.drawdownAmount != null
                            ? selfFundingState.drawdownAmount.toLocaleString('en-US')
                            : ''
                        }
                        onChange={e => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          setSelfFundingState(prev => ({
                            ...prev,
                            drawdownAmount: raw ? Number(raw) : null,
                          }));
                        }}
                      />
                    </InputGroup>
                    <ToggleGroup
                      type="single"
                      value={selfFundingState.drawdownAmountInterval ?? 'yearly'}
                      onValueChange={val => {
                        if (val) {
                          setSelfFundingState(prev => ({
                            ...prev,
                            drawdownAmountInterval: val as 'monthly' | 'yearly',
                          }));
                        }
                      }}
                      variant="outline"
                      className="shrink-0"
                    >
                      <ToggleGroupItem value="monthly">Monthly</ToggleGroupItem>
                      <ToggleGroupItem value="yearly">Yearly</ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPartnerSection && (
        <div className="space-y-3 border-t pt-4">
          {isExistingMirror ? (
            <MirroredSourceStatus
              clientName={clientName}
              partnerName={partnerFirstName ?? ''}
              splitRatio={existingSplitRatio}
              totalAssets={totalAssets}
              onUnlink={() => setShowUnlinkConfirm(true)}
            />
          ) : (
            <PartnerAllocationSection
              enabled={mirrorChecked}
              onEnabledChange={checked => {
                setMirrorChecked(checked);
                if (!checked) {
                  setAllocationOption('mirror');
                  setCustomClientPercent(50);
                }
              }}
              value={totalAssets}
              clientName={clientName}
              partnerName={partnerFirstName ?? ''}
              selectedOption={allocationOption}
              onOptionChange={setAllocationOption}
              customClientPercent={customClientPercent}
              onCustomClientPercentChange={setCustomClientPercent}
              className="border-t-0 px-0 pb-0 pt-0"
            />
          )}
        </div>
      )}

      {!formIsValid && (
        <FormErrorAlert mainErrorMessage="There is an error with your submission">
          {hasRequiredFieldsErrors && (
            <li>
              <span className="font-bold">Required field:</span> Please fill out all required
              fields.
            </li>
          )}
        </FormErrorAlert>
      )}
    </div>
  );

  const submitButton = (
    <Button
      className="w-full"
      onClick={handleClickSubmit}
    >
      Submit
    </Button>
  );

  return (
    <>
      <UnsavedChangesDialog
        showWarning={showWarning}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
      />
      <UnsavedChangesDialog
        showWarning={showUnlinkConfirm}
        handleCancelClose={() => setShowUnlinkConfirm(false)}
        handleConfirmClose={() => {
          setShowUnlinkConfirm(false);
          if (fundingSourceId) {
            handleUnlink(fundingSourceId, selfFunding as SelfFundingSource);
            setOpen(false);
          }
        }}
        dialogTitle={`Unlink from ${partnerFirstName}'s plan?`}
        dialogDescription={`This will disconnect the two accounts. ${clientName}'s and ${partnerFirstName}'s accounts will become independent and future changes to one will no longer update the other.`}
        primaryButtonText="Unlink"
        secondaryButtonText="Cancel"
        destructive={false}
      />
      {isMobile ? (
        <Drawer
          open={open}
          onOpenChange={onClose}
        >
          <DrawerContent className="max-h-[90vh] overflow-y-auto px-4">
            <DrawerHeader className="text-left">
              <DrawerTitle>Self-Funding Details</DrawerTitle>
              <DrawerDescription>Configure your self-funding source</DrawerDescription>
            </DrawerHeader>
            <div className="py-4">{formContent}</div>
            <DrawerFooter className="px-0">{submitButton}</DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={open}
          onOpenChange={onClose}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Self-Funding Details</DialogTitle>
              <DialogDescription>Configure your self-funding source</DialogDescription>
            </DialogHeader>
            <div className="py-2">{formContent}</div>
            {submitButton}
          </DialogContent>
        </Dialog>
      )}

      <Sheet
        open={showFormulaSheet}
        onOpenChange={setShowFormulaSheet}
      >
        <SheetContent
          side="right"
          className="overflow-y-auto sm:max-w-lg"
        >
          <SheetHeader>
            <SheetTitle>{formulaInfo.title}</SheetTitle>
            <SheetDescription>{assetLabel}</SheetDescription>
          </SheetHeader>

          <CalcFormulaSheetContent
            assetTypeId={formulaAssetTypeId}
            label={assetLabel}
            hasContributions={
              isOther
                ? (selfFundingState.monthlyContributionAmount ?? 0) > 0
                : (yearlyContributionValue ?? 0) > 0
            }
            hasContributionStop={selfFundingState.contributionStopYear != null}
            hasWithdrawals={
              !!selfFundingState.hasDrawdown &&
              selfFundingState.drawdownAmount != null &&
              selfFundingState.drawdownAmount > 0
            }
            isRetirementIncome={isRetirementIncome}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
